import { TGoogleMeetingRoom } from "apis/meetings/meeting-room";

export const googleLogin = () => {
  const scopes = ["https://www.googleapis.com/auth/userinfo.profile", "https://www.googleapis.com/auth/userinfo.email"];
  const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${
    process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID
  }&redirect_uri=${process.env.REACT_APP_WEB_URL}/google/redirect/login&scope=${scopes.join(" ")}&access_type=offline&prompt=select_account`;

  window.location.href = authUrl;
};

// FEAT: (new) 구글 dictionary API를 통해 멤버 이메일 리스트 가져오기
export const getMemberEmailList = async (googleAccessToken: string) => {
  let emails: string[] = [];
  let nextPageToken: string | null = null;

  try {
    do {
      const url =
        nextPageToken !== null
          ? "https://www.googleapis.com/admin/directory/v1/users?customer=my_customer&maxResults=100&orderBy=email&pageToken=" +
            nextPageToken
          : "https://www.googleapis.com/admin/directory/v1/users?customer=my_customer&maxResults=100&orderBy=email";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${googleAccessToken}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data: any = await response.json();
        emails.push(...data.users.map((user: any) => user.primaryEmail));
        nextPageToken = data.nextPageToken || null;
      } else {
        if (response.status === 403) {
          return { success: false, message: "멤버를 불러올 수 있는 Google Workspace 권한이 없습니다." };
        } else {
          return { success: false, message: "멤버 목록을 가져오는데 실패하였습니다. 다시 시도해주세요." };
        }
      }
    } while (nextPageToken !== null);

    return { success: true, email: emails };
  } catch (error: any) {
    return { success: false, message: "멤버 목록을 가져오는데 실패하였습니다. 다시 시도해주세요." };
  }
};

//UPDATE: (new) 구글 calendar 중 write 권한이 있는 resource data 가져오기
export const getEnableWriteGoogleCalendarResource = async (googleAccessToken: string) => {
  try {
    const writableCalendars: any[] = [];
    let nextPageToken: string | undefined = undefined;
    const maxResults = 100;

    do {
      const url = new URL("https://www.googleapis.com/calendar/v3/users/me/calendarList");
      url.searchParams.set("maxResults", maxResults.toString());
      if (nextPageToken) {
        url.searchParams.set("pageToken", nextPageToken);
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${googleAccessToken}`,
          Accept: "application/json",
        },
      });

      const data: any = await response.json();
      writableCalendars.push(
        ...data.items
          .filter((item: any) => item.accessRole === "writer" || item.accessRole === "owner")
          .filter((item: any) => item.id.includes("resource.calendar.google.com"))
      );
      nextPageToken = data.nextPageToken;
    } while (nextPageToken);

    //INFO: 캘린더 내용 상세 조회 & 정제
    const resourceList = await getGoogleCalendarResourceList(googleAccessToken);

    if (!resourceList.success) return undefined;
    else {
      const result: TGoogleMeetingRoom[] = writableCalendars
        .map((calendar: any) => {
          const resource = resourceList.data?.find((item: any) => item.resourceEmail === calendar.id);
          return resource;
        })
        .filter((item: any) => item !== undefined);

      return { success: true, data: result };
    }
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return {
        success: false,
        message: "Google Workspace 리소스 캘린더를 불러올 수 있는 권한이 없습니다.",
      };
    } else return { success: false, error: "리소스 캘린더를 불러오는데 실패하였습니다. 다시 시도해주세요." };
  }
};

//INFO: (new) 개인 calendar불러오기
export const getEnableWriteGoogleCalendar = async (googleAccessToken: string) => {
  try {
    const response = await fetch("https://www.googleapis.com/calendar/v3/calendars/primary", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${googleAccessToken}`,
        Accept: "application/json",
      },
    });

    const data = await response.json();
    return { success: response.ok, data: [data] };
  } catch (error: any) {
    return { success: false, message: "캘린더 목록을 가져오는데 실패하였습니다. 다시 시도해주세요." };
  }
};

const getGoogleCalendarResourceList = async (googleAccessToken: string) => {
  const allResources: any[] = [];
  let nextPageToken: string | undefined = undefined;
  const maxResults = 100;

  try {
    do {
      const url = new URL("https://www.googleapis.com/admin/directory/v1/customer/my_customer/resources/calendars");
      url.searchParams.set("maxResults", maxResults.toString());
      if (nextPageToken) {
        url.searchParams.set("pageToken", nextPageToken);
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${googleAccessToken}`,
          Accept: "application/json",
        },
      });

      const data: any = await response.json();

      if (data.items) {
        allResources.push(...data.items);
      }

      nextPageToken = data.nextPageToken;
    } while (nextPageToken);

    return { success: true, data: allResources };
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 403) {
      return {
        success: false,
        message: "Google Workspace 리소스 캘린더를 불러올 수 있는 권한이 없습니다.",
      };
    } else {
      return {
        success: false,
        error: "리소스 캘린더를 불러오는데 실패하였습니다. 다시 시도해주세요.",
      };
    }
  }
};

//* INFO: 구글 organization unit 리스트 가져오기
export const getOrganizationUnitList = async (googleAccessToken: string) => {
  try {
    const response = await fetch(
      "https://www.googleapis.com/admin/directory/v1/customer/my_customer/orgunits?type=all",
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${googleAccessToken}`,
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();
    return { success: true, data: data };
  } catch (error: any) {
    return {
      success: false,
      message: "Google Workspace 조직 목록을 불러올 수 있는 권한이 없습니다. 연동을 해제한 뒤 다시 연동해 주세요.",
    };
  }
};
